import type { InventarisationIncome } from "@loanlink-nl/model-app";
import { fuzzyStringToNum } from "@loanlink-nl/model-mapping";
import { fuzzyDateParse } from "@loanlink-nl/migration";

/**
 * Helper function to check if pension income is active on a given date
 * Pension is counted when:
 * - pensionStartDate <= asOfDate (pension has started or starts on this date)
 * - paymentEndDate is empty OR paymentEndDate >= asOfDate (pension hasn't ended yet)
 */
function isPensionActiveAtDate(
	pensionStartDate: string | undefined,
	paymentEndDate: string | undefined,
	asOfDate: string,
): boolean {
	if (!asOfDate) {
		return true;
	}

	const asOfDateTime = fuzzyDateParse(asOfDate);
	if (!asOfDateTime.isValid) {
		return true;
	}

	if (pensionStartDate) {
		const startDateTime = fuzzyDateParse(pensionStartDate);
		if (startDateTime.isValid && startDateTime > asOfDateTime) {
			return false;
		}
	}

	if (paymentEndDate) {
		const endDateTime = fuzzyDateParse(paymentEndDate);
		if (endDateTime.isValid && endDateTime < asOfDateTime) {
			return false;
		}
	}

	return true;
}

/**
 * Calculates the total yearly income for a specific customer from inventarisation data
 */
export function calculateCustomerYearlyIncome(
	incomeData: InventarisationIncome,
	customerId: string,
	asOfDate?: string,
): number {
	const customerIncome = incomeData.byCustomer.find(
		(customer) => customer.customerId === customerId,
	);

	if (!customerIncome) {
		return 0;
	}

	let totalIncome = 0;

	// Calculate income from loondienst (employment)
	for (const loondienstEntry of customerIncome.sections.loondienst ?? []) {
		const loondienst = loondienstEntry.values;
		if (loondienst?.calculationMethod === "uwvVerzekeringsbericht") {
			totalIncome += fuzzyStringToNum(loondienst.uwvToetsinkomen) || 0;
		} else {
			const salaryMultiplier =
				loondienst?.salaryPeriod === "jaar"
					? 1
					: loondienst?.salaryPeriod === "maand"
						? 12
						: loondienst?.salaryPeriod === "4weken"
							? 13
							: 0;
			const salaryPerYear =
				(fuzzyStringToNum(loondienst?.grossSalary) || 0) * salaryMultiplier;

			let yearlyGross = salaryPerYear;

			if (!loondienst?.salaryIncludesVacation) {
				// Bij exclusief: vakantietoeslag apart optellen
				yearlyGross += fuzzyStringToNum(loondienst?.vacationAllowance) || 0;
			}
			// Bij inclusief: salaryPerYear bevat al de vakantietoeslag

			yearlyGross += fuzzyStringToNum(loondienst?.thirteenthMonth) || 0;
			yearlyGross += fuzzyStringToNum(loondienst?.overtime) || 0;
			yearlyGross += fuzzyStringToNum(loondienst?.irregularHoursAllowance) || 0;
			yearlyGross += fuzzyStringToNum(loondienst?.commission) || 0;
			yearlyGross += fuzzyStringToNum(loondienst?.yearEndBonus) || 0;

			// Apply percentage for toetsinkomen
			const percentage =
				fuzzyStringToNum(loondienst?.percentageForToetsinkomen) || 1;
			totalIncome += yearlyGross * percentage;
		}
	}

	// Calculate income from uitkering (benefits)
	for (const uitkeringEntry of customerIncome.sections.uitkering ?? []) {
		const uitkering = uitkeringEntry.values;
		const yearlyBenefit =
			(fuzzyStringToNum(uitkering?.grossYearlyBenefit) || 0) +
			(fuzzyStringToNum(uitkering?.vacationBenefit) || 0);
		const percentage = fuzzyStringToNum(uitkering?.percentageCounted) || 1;
		totalIncome += yearlyBenefit * percentage;
	}

	// Calculate income from pensioen (pension) - only include if active on the asOfDate
	for (const pensioenEntry of customerIncome.sections.pensioen ?? []) {
		const pensioen = pensioenEntry.values;

		// Check if pension is active on the asOfDate
		if (
			!isPensionActiveAtDate(
				pensioen?.pensionStartDate,
				pensioen?.paymentEndDate,
				asOfDate ?? "",
			)
		) {
			continue;
		}

		const percentage = fuzzyStringToNum(pensioen?.percentageCounted) || 1;
		totalIncome +=
			(fuzzyStringToNum(pensioen?.grossYearlyPayment) || 0) * percentage;
	}

	// Calculate income from onderneming (business)
	for (const ondernemingEntry of customerIncome.sections.onderneming ?? []) {
		const onderneming = ondernemingEntry.values;
		const assessedIncome =
			fuzzyStringToNum(onderneming?.assessedIncome) ||
			fuzzyStringToNum(onderneming?.averageResult) ||
			0;
		const percentage = fuzzyStringToNum(onderneming?.percentageCounted) || 1;
		totalIncome += assessedIncome * percentage;
	}

	// Calculate income from overig (other sources)
	for (const overigEntry of customerIncome.sections.overig ?? []) {
		const overig = overigEntry.values;
		const percentage = fuzzyStringToNum(overig?.percentageCounted) || 1;
		totalIncome +=
			(fuzzyStringToNum(overig?.grossYearlyPayment) || 0) * percentage;
	}

	// Calculate income from IVO (Inkomensverklaring Ondernemer).
	// Only the first IVO entry counts, matching the HDN25/HDN26 mappers.
	// The IVO assessedIncome ("Toetsinkomen") is added alongside the other
	// income categories.
	const firstIvo = customerIncome.sections.ivo?.[0]?.values;
	if (firstIvo) {
		totalIncome += fuzzyStringToNum(firstIvo.assessedIncome) || 0;
	}

	return totalIncome;
}

/**
 * Calculates the total yearly income for the actual dossier customers.
 *
 * The ordered list of current customer ids (`data.customers.map((c) => c.id)`)
 * is required and drives which income entries are used as customer 1 and
 * customer 2. This prevents stale "ghost" records in `income.byCustomer` from
 * influencing the calculation; only the (at most two) actual customers count,
 * in the order they appear in the dossier.
 */
export function calculateTotalYearlyIncome(
	incomeData: InventarisationIncome,
	customerIds: string[],
	asOfDate?: string,
): {
	customer1Income: number;
	customer2Income: number;
	totalIncome: number;
} {
	const customer1Id = customerIds[0];
	const customer2Id = customerIds[1];

	const customer1Income = customer1Id
		? calculateCustomerYearlyIncome(incomeData, customer1Id, asOfDate)
		: 0;

	const customer2Income = customer2Id
		? calculateCustomerYearlyIncome(incomeData, customer2Id, asOfDate)
		: 0;

	return {
		customer1Income,
		customer2Income,
		totalIncome: customer1Income + customer2Income,
	};
}
